import { R4 } from "@ahryman40k/ts-fhir-types";

import { ConfidentialityCode, DeletionStatus, FormatCode } from "@/global";
import { getConfidentialityProps, getDeletionStatusExtension, hexToBytes } from "@/utils";
import ValueSetDictionaries from "@/valueSets/ValueSetDictionaries";
import { OriginalProviderRole } from "@/valueSets/ValueSetEnums";

import {
  FHIR_EXT_AUTHOR_ROLE,
  FHIR_EXT_ENTRY_CROSS_REF_STU5,
  FHIR_EXT_MERGING_CONFLICT,
  FHIR_EXT_ORIGINAL_COMPOSITION_UUID,
  ORIGINAL_PROVIDER_ROLE_SYSTEM,
} from "../constants";
import { DocumentBundle, VaccinationResource, vaccinationResourceTypes } from "../types";

export function getRandomIdentifier(): R4.IIdentifier {
  return {
    system: "urn:ietf:rfc:3986",
    value: `urn:uuid:${crypto.randomUUID()}`,
  };
}

export function getConfidentialityCoding(confidentialityCode: ConfidentialityCode): R4.ICoding {
  return {
    system: "http://snomed.info/sct",
    code: confidentialityCode,
    display: ConfidentialityCodes[confidentialityCode].display,
  };
}

export const ConfidentialityCodes = {
  [ConfidentialityCode.Normal]: {
    display: "Normal (qualifier value)",
    classification: "N",
  },
  [ConfidentialityCode.Restricted]: {
    display: "Restricted (qualifier value)",
    classification: "R",
  },
  [ConfidentialityCode.Secret]: {
    display: "Secret (qualifier value)",
    classification: "V",
  },
};

export function setConfidentiality(
  composition: R4.IComposition,
  confidentialityCode: ConfidentialityCode
) {
  composition.confidentiality = ConfidentialityCodes[confidentialityCode].classification;
  composition._confidentiality = {
    extension: [
      {
        url: "http://fhir.ch/ig/ch-core/StructureDefinition/ch-ext-epr-confidentialitycode",
        valueCodeableConcept: {
          coding: [getConfidentialityCoding(confidentialityCode)],
        },
      },
    ],
  };
}

export function getSectionByCode(
  bundle: R4.IBundle,
  sectionCode: string
): R4.IComposition_Section | null {
  const composition = bundle.entry?.[0].resource as R4.IComposition;
  if (!composition) {
    return null;
  }

  // Find the section with the specified code
  return (
    composition.section?.find((section) =>
      section.code?.coding?.some((coding) => coding.code === sectionCode)
    ) || null
  );
}

export function getSectionEntries(bundle: R4.IBundle, sectionCode: string) {
  const composition = bundle.entry?.[0].resource as R4.IComposition;
  if (!composition) {
    return [];
  }

  // Find the section with the specified code
  const section = composition.section?.find((section) =>
    section.code?.coding?.some((coding) => coding.code === sectionCode)
  );

  const entries = section?.entry;
  if (!entries) {
    return [];
  }

  const resources = [];

  // Resolve the entry references
  for (const entry of entries) {
    const ref = entry.reference;
    if (!ref) {
      continue;
    }
    const resource = getResourceByReference(bundle, ref);

    if (resource) {
      resources.push(resource);
    }
  }

  return resources;
}

export function humanNameToString(name: R4.IHumanName): string {
  const nameParts = [];

  if (name.prefix) {
    nameParts.push(...name.prefix);
  }
  if (name.given) {
    nameParts.push(...name.given);
  }
  if (name.family) {
    nameParts.push(name.family);
  }
  if (name.suffix) {
    nameParts.push(...name.suffix);
  }

  if (nameParts.length === 0 && name.text) {
    return name.text;
  }

  return nameParts.join(" ");
}

export function getResourceByReference(bundle: R4.IBundle, reference: string): R4.IResource | null {
  if (reference.startsWith("urn:uuid:")) {
    return bundle.entry?.find((entry) => entry.fullUrl === reference)?.resource || null;
  }

  const [type, id] = reference.split("/");
  return (
    bundle.entry?.find(
      (entry) => entry.resource?.resourceType === type && entry.resource?.id === id
    )?.resource || null
  );
}

export function getRecorder(
  bundle: R4.IBundle,
  resource: R4.ICondition | R4.IAllergyIntolerance
): R4.IPractitionerRole | R4.IPractitioner | R4.IPatient | R4.IRelatedPerson | null {
  const recorderRef = resource.recorder?.reference;
  if (!recorderRef) {
    return null;
  }

  return getResourceByReference(bundle, recorderRef) as
    | R4.IPractitionerRole
    | R4.IPractitioner
    | R4.IPatient
    | R4.IRelatedPerson
    | null;
}

export function getRecorderName(
  bundle: R4.IBundle,
  resource: R4.ICondition | R4.IAllergyIntolerance
): string {
  const recorder = getRecorder(bundle, resource);
  if (recorder?.resourceType === "PractitionerRole") {
    return practitionerRoleToString(recorder, bundle);
  } else if (recorder?.resourceType === "Practitioner") {
    return practitionerToString(recorder);
  } else if (recorder?.resourceType === "Patient") {
    return patientToString(recorder);
  } else if (recorder?.resourceType === "RelatedPerson") {
    return relatedPersonToString(recorder);
  }
  return "";
}

export function getPerformer(
  bundle: R4.IBundle,
  immunization: R4.IImmunization
): R4.IPractitionerRole | null {
  const performerRef = immunization.performer?.[0]?.actor.reference;
  if (!performerRef) {
    return null;
  }

  return getResourceByReference(bundle, performerRef) as R4.IPractitionerRole | null;
}

export function getPerformerName(bundle: R4.IBundle, immunization: R4.IImmunization): string {
  const performer = getPerformer(bundle, immunization);
  if (!performer) {
    return "";
  }

  return practitionerRoleToString(performer, bundle);
}

export function relatedPersonToString(relatedPerson: R4.IRelatedPerson): string {
  if (relatedPerson?.name?.[0]) {
    return humanNameToString(relatedPerson.name?.[0]);
  }
  return "";
}

export function patientToString(patient: R4.IPatient): string {
  if (patient?.name?.[0]) {
    return humanNameToString(patient.name?.[0]);
  }
  return "";
}

export function practitionerToString(practitioner: R4.IPractitioner): string {
  if (practitioner?.name?.[0]) {
    return humanNameToString(practitioner.name?.[0]);
  }
  return "";
}

export function practitionerRoleToString(
  practitionerRole: R4.IPractitionerRole,
  bundle: R4.IBundle
): string {
  let practitionerName = null;
  if (practitionerRole.practitioner?.reference) {
    const practitioner = getResourceByReference(
      bundle,
      practitionerRole.practitioner?.reference
    ) as R4.IPractitioner | null;
    if (practitioner?.name?.[0]) {
      practitionerName = humanNameToString(practitioner.name?.[0]);
    }
  }

  let organizationName = null;
  if (practitionerRole.organization?.reference) {
    const organization = getResourceByReference(
      bundle,
      practitionerRole.organization?.reference
    ) as R4.IOrganization | null;
    if (organization?.name && organization.name !== "-") {
      organizationName = organization.name;
    }
  }

  if (practitionerName && organizationName) {
    return `${practitionerName} (${organizationName})`;
  } else if (practitionerName) {
    return practitionerName;
  } else if (organizationName) {
    return organizationName;
  } else {
    return "";
  }
}

export function getAnnotationAuthorString(annotation: R4.IAnnotation, bundle: R4.IBundle): string {
  if (annotation.authorString) {
    return annotation.authorString;
  }

  const author = getAnnotationAuthor(annotation, bundle);
  if (!author) {
    return "";
  }

  if (author.resourceType === "Practitioner") {
    return practitionerToString(author);
  } else if (author.resourceType === "Patient") {
    return patientToString(author);
  } else if (author.resourceType === "RelatedPerson") {
    return relatedPersonToString(author);
  } else if (author.resourceType === "Organization") {
    return author.name || "";
  }

  return "";
}

export function getAnnotationAuthor(
  annotation: R4.IAnnotation,
  bundle: R4.IBundle
): R4.IPractitioner | R4.IPatient | R4.IRelatedPerson | R4.IOrganization | null {
  const authorRef = annotation.authorReference?.reference;
  if (!authorRef) {
    return null;
  }

  return getResourceByReference(bundle, authorRef) as
    | R4.IPractitioner
    | R4.IPatient
    | R4.IRelatedPerson
    | R4.IOrganization
    | null;
}

export function createImmunizationAdministrationBundle(patient?: R4.IPatient): DocumentBundle {
  const bundleId = "Bundle-0001";
  const compositionId = "Composition-0001";
  const now = new Date().toISOString();

  const composition: R4.IComposition = {
    resourceType: "Composition",
    id: compositionId,
    meta: {
      profile: [
        "http://fhir.ch/ig/ch-vacd/StructureDefinition/ch-vacd-composition-immunization-administration",
      ],
    },
    language: "en-US",
    identifier: getRandomIdentifier(),
    status: R4.CompositionStatusKind._final,
    type: {
      coding: [
        {
          system: "http://snomed.info/sct",
          code: "41000179103",
          display: "Immunization record",
        },
      ],
    },
    category: [
      {
        coding: [
          {
            system: "urn:oid:2.16.756.5.30.1.127.3.10.10",
            code: "urn:che:epr:ch-vacd:immunization-administration:2022",
            display: "CH VACD Immunization Administration",
          },
        ],
      },
    ],
    date: now,
    author: [],
    title: "Immunization Administration",
    ...getConfidentialityProps(),
    section: [],
  };

  const bundle: DocumentBundle = {
    resourceType: "Bundle",
    id: bundleId,
    meta: {
      profile: [
        "http://fhir.ch/ig/ch-vacd/StructureDefinition/ch-vacd-document-immunization-administration",
      ],
    },
    identifier: getRandomIdentifier(),
    type: R4.BundleTypeKind._document,
    timestamp: now,
    entry: [makeEntry(composition) as R4.IBundle_Entry & { resource: R4.IComposition }],
  };

  if (patient) {
    composition.subject = makeUuidUrnReference(patient);
    bundle.entry.push(makeEntry(patient));
  }

  return bundle;
}

export async function createVaccinationDocumentReference(
  bundle: R4.IBundle,
  authorName: R4.IHumanName,
  confidentialityCode: ConfidentialityCode,
  formatCode: FormatCode
): Promise<R4.IDocumentReference> {
  const suffix = new Uint8Array(hexToBytes(crypto.randomUUID().replaceAll("-", "")));
  const masterIdentifier: R4.IIdentifier = {
    value: `2.25.${suffix.join("")}`,
    system: "urn:ietf:rfc:3986",
  };

  const identifier: R4.IIdentifier[] = [getRandomIdentifier()];

  const id = identifier[0].value!;
  const status: R4.DocumentReferenceStatusKind = R4.DocumentReferenceStatusKind._current;

  const typeCode = "41000179103";
  const type = {
    coding: [ValueSetDictionaries.typeCode[typeCode]],
  };

  const classCode = "184216000";
  const category = [
    {
      coding: [ValueSetDictionaries.classCode[classCode]],
    },
  ];

  const context: R4.IDocumentReference_Context = {
    facilityType: { coding: [ValueSetDictionaries.healthcareFacilityTypeCode["43741000"]] },
    practiceSetting: { coding: [ValueSetDictionaries.practiceSettingCode["394802001"]] },
  };

  const authorRefs: R4.IReference[] = [];
  const containedResources: R4.IResourceList[] = [];
  let containedIndexCounter = 1;

  const practitionerRole: R4.IPractitionerRole = {
    resourceType: "PractitionerRole",
    id: `${containedIndexCounter++}`,
  };

  const practitioner: R4.IPractitioner = {
    resourceType: "Practitioner",
    id: `${containedIndexCounter++}`,
    name: [authorName],
  };

  practitionerRole.practitioner = {
    reference: `#${practitioner.id}`,
  };

  containedResources.push(practitioner);
  containedResources.push(practitionerRole);
  authorRefs.push({
    reference: `#${practitionerRole.id}`,
  });

  const securityLabel: R4.ICodeableConcept[] = [
    {
      coding: [ValueSetDictionaries.confidentialityCode[confidentialityCode]],
    },
  ];

  const blob = bundleToBlob(bundle);
  const hash = await getBase64Hash(blob);
  const title = (bundle.entry?.[0].resource as R4.IComposition)?.title;

  const content: R4.IDocumentReference_Content[] = [
    {
      attachment: {
        contentType: "application/fhir+json",
        language: "en",
        hash,
        url: `urn:uuid:${crypto.randomUUID()}`,
        size: blob.size,
        title,
        creation: new Date().toISOString(),
      },
      format: ValueSetDictionaries.formatCode[formatCode],
    },
  ];

  const extension: R4.IExtension[] = [];
  if (formatCode === FormatCode.ImmunizationAdministration) {
    extension.push(getDeletionStatusExtension(DeletionStatus.DeletionProhibited));
  }

  return {
    resourceType: "DocumentReference",
    id,
    masterIdentifier,
    identifier,
    status,
    contained: containedResources,
    author: authorRefs,
    type,
    category,
    context,
    securityLabel,
    content,
    extension,
  };
}

export async function getBase64Hash(
  blob: Blob,
  algorithm: AlgorithmIdentifier = "SHA-1"
): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest(algorithm, buffer);
  const byteArray = new Uint8Array(hashBuffer);
  return btoa(String.fromCharCode(...byteArray));
}

export function setMetaExtension(resource: R4.IResource, extension: R4.IExtension) {
  if (!resource.meta) {
    resource.meta = {};
  }

  if (!resource.meta.extension) {
    resource.meta.extension = [];
  }

  // Remove existing extension to prevent duplicates
  resource.meta.extension = resource.meta.extension.filter((ext) => ext.url !== extension.url);

  resource.meta.extension.push(extension);
}

export function getMetaExtension(
  resource: R4.IResource,
  extensionUrl: string
): R4.IExtension | undefined {
  return resource.meta?.extension?.find((ext) => ext.url === extensionUrl);
}

export function deleteMetaExtensions(resource?: R4.IResource | null) {
  if (resource?.meta?.extension) {
    delete resource.meta.extension;
  }
}

export function setCompositionUuidExtension(resource: R4.IResource, compositionUuid: string) {
  setMetaExtension(resource, {
    url: FHIR_EXT_ORIGINAL_COMPOSITION_UUID,
    valueString: compositionUuid,
  });
}

export function getCompositionUuid(resource: R4.IResource): string | undefined {
  return getMetaExtension(resource, FHIR_EXT_ORIGINAL_COMPOSITION_UUID)?.valueString;
}

export function addReplacesExtension(
  resource: VaccinationResource,
  entryUuid?: string,
  compositionUuid?: string
) {
  if (!resource.extension) {
    resource.extension = [];
  }

  const extension: R4.IExtension = {
    url: FHIR_EXT_ENTRY_CROSS_REF_STU5,
    extension: [
      {
        url: "relationcode",
        valueCode: "replaces",
      },
    ],
  };

  if (entryUuid) {
    extension.extension!.push({
      url: "entry",
      valueReference: {
        type: resource.resourceType,
        identifier: {
          value: entryUuid,
        },
      },
    });
  }

  if (compositionUuid) {
    extension.extension!.push({
      url: "container",
      valueReference: {
        type: "Composition",
        identifier: {
          value: compositionUuid,
        },
      },
    });
  }

  resource.extension.push(extension);
}

export function replaceResource(
  newComposition: R4.IComposition,
  newResource: VaccinationResource,
  oldResource: VaccinationResource
): void {
  const compositionUuid = getCompositionUuid(oldResource);
  const entryUuid = oldResource.identifier?.[0].value;

  addReplacesExtension(newResource, entryUuid, compositionUuid);

  newComposition.relatesTo = [
    {
      code: "replaces",
      targetReference: {
        reference: compositionUuid,
      },
    },
  ];
}

export function setDuplicateExtension(
  resource: VaccinationResource,
  duplicateResource: VaccinationResource
) {
  if (!resource.extension) {
    resource.extension = [];
  }

  // Remove existing extension to prevent duplicates
  resource.extension = resource.extension.filter((ext) => ext.url !== FHIR_EXT_MERGING_CONFLICT);

  const extension = {
    url: FHIR_EXT_MERGING_CONFLICT,
    extension: [
      {
        url: "conflict",
        valueCode: "duplicate",
      },
      {
        url: "entry",
        valueReference: makeUuidUrnReference(duplicateResource),
      },
    ],
  };

  resource.extension.push(extension);
}

export function isDuplicate(resource: VaccinationResource): boolean {
  return !!resource.extension?.some((ext) => ext.url === FHIR_EXT_MERGING_CONFLICT);
}

export function isDeleted(resource: VaccinationResource): boolean {
  switch (resource.resourceType) {
    case "AllergyIntolerance":
    case "Condition":
      return !!resource.verificationStatus?.coding?.some(
        (coding) => coding.code === "entered-in-error"
      );
    case "Immunization":
    case "Observation":
      return resource.status === "entered-in-error";
    default:
      return false;
  }
}

export function bundleToBlob(bundle: R4.IBundle): Blob {
  return new Blob([JSON.stringify(bundle, null, 2)], { type: "application/fhir+json" });
}

export function isVaccinationResource(resource?: any | null): boolean {
  if (!resource || !resource.resourceType) {
    return false;
  }

  return vaccinationResourceTypes.includes(resource.resourceType);
}

export function isVerified(resource: VaccinationResource): boolean {
  const authorRole = getAuthorRole(resource);

  // TODO: Include TCU?
  return !!authorRole && [OriginalProviderRole.HCP, OriginalProviderRole.ASS].includes(authorRole);
}

/**
 * TODO: Replace with https://fhir.ch/ig/ch-epr-fhir/StructureDefinition-ch-ext-author-authorrole.html
 */
export function getAuthorRoleFromReference(
  reference: R4.IDocumentReference
): OriginalProviderRole | null {
  const extraMetadataExt = reference.extension?.find(
    (extension) =>
      extension.url === "https://api.phellowseven.com/fhir/StructureDefinition/extraMetadata"
  );
  if (!extraMetadataExt) {
    return null;
  }

  const authorRole = extraMetadataExt.extension?.find(
    (extension) => extension.url === "urn:e-health-suisse:2020:originalProviderRole"
  );
  return (authorRole?.valueString?.split("^^^")[0] as OriginalProviderRole) || null;
}

export function setAuthorRoleExtension(
  resource: VaccinationResource,
  authorRoleCode: OriginalProviderRole
) {
  setMetaExtension(resource, {
    url: FHIR_EXT_AUTHOR_ROLE,
    valueCoding: {
      system: ORIGINAL_PROVIDER_ROLE_SYSTEM,
      code: authorRoleCode,
    },
  });
}

export function getAuthorRole(resource: R4.IResource): OriginalProviderRole | undefined {
  return getMetaExtension(resource, FHIR_EXT_AUTHOR_ROLE)?.valueCoding?.code as
    | OriginalProviderRole
    | undefined;
}

export function addAuthorRoleToVaccinationResources(
  bundle: R4.IBundle,
  authorRoleCode: OriginalProviderRole
) {
  bundle.entry?.forEach((entry) => {
    if (isVaccinationResource(entry.resource)) {
      const resource = entry.resource as VaccinationResource;
      setAuthorRoleExtension(resource, authorRoleCode);
    }
  });
}

export function addEntryIfNotExists(bundle: R4.IBundle, entry: R4.IBundle_Entry): R4.IBundle {
  if (!bundle.entry) {
    bundle.entry = [];
  }

  if (bundle.entry.some((existingEntry) => existingEntry.fullUrl === entry.fullUrl)) {
    return bundle;
  }

  bundle.entry.push(entry);
  return bundle;
}

export function getUuidUrnReferenceValue(resource: R4.IResourceList): string {
  if (!resource.id) {
    return "";
  }
  return `urn:uuid:${resource.id}`;
}

export function makeEntry(resource: R4.IResourceList): R4.IBundle_Entry {
  return {
    fullUrl: getUuidUrnReferenceValue(resource),
    resource,
  };
}

export function makeUuidUrnReference(resource: R4.IResourceList): R4.IReference {
  return {
    reference: getUuidUrnReferenceValue(resource),
  };
}

// Add a resource as an entry, but only if it does not already exist in the bundle
export function addResourceToBundle(bundle: R4.IBundle, resource: R4.IResourceList): R4.IBundle {
  const entry = makeEntry(resource);
  return addEntryIfNotExists(bundle, entry);
}

export function copyReferencedAnnotationAuthors(
  note: R4.IAnnotation[],
  sourceBundle: R4.IBundle,
  targetBundle: R4.IBundle
) {
  note.forEach((note: R4.IAnnotation) => {
    if (note.authorReference?.reference) {
      const author = getResourceByReference(sourceBundle, note.authorReference.reference) as
        | R4.IPractitioner
        | R4.IPatient
        | R4.IRelatedPerson
        | R4.IOrganization
        | undefined;

      if (author) {
        addResourceToBundle(targetBundle, author);
      }
    }
  });
}

/**
 *
 * @param notes R4.IAnnotation[]
 * @returns A copy of the notes array sorted by time in ascending order.
 */
export function sortNotes(notes: R4.IAnnotation[]): R4.IAnnotation[] {
  const notesCopy = [...notes];
  return notesCopy.sort((a, b) => {
    const dateA = a.time ? new Date(a.time).getTime() : 0;
    const dateB = b.time ? new Date(b.time).getTime() : 0;
    return dateA - dateB;
  });
}

export function isIdentical(resourceA: object, resourceB: object): boolean {
  if (!resourceA || !resourceB) {
    return false;
  }
  return JSON.stringify(resourceA) === JSON.stringify(resourceB);
}
